import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { useListParams } from "hooks/useListParams"
import {
  useDeletePrayerReminderSetting,
  usePrayerReminderSettings,
} from "hooks/prayerReminderHook"
import PrayerReminderSettingModal from "./PrayerReminderSettingModal"

const PRAYER_LABELS = {
  fajr: "Fajr",
  dhuhr: "Dhuhr",
  asr: "Asr",
  maghrib: "Maghrib",
  isha: "Isha",
}

const prayerSummary = prayers => {
  if (!prayers) return "—"
  return Object.entries(prayers)
    .filter(([, cfg]) => cfg?.enabled || cfg?.jamaat_enabled)
    .map(([key, cfg]) => {
      const parts = []
      if (cfg.enabled) {
        const offset = Number(cfg.offset_minutes) || 0
        parts.push(offset ? `${offset > 0 ? "+" : ""}${offset}m` : "waqt")
      }
      if (cfg.jamaat_enabled) {
        const after = Number(cfg.jamaat_after_minutes) || 0
        const lead = Number(cfg.jamaat_lead_minutes) || 0
        parts.push(`jamaat +${after}m (−${lead}m)`)
      }
      return `${PRAYER_LABELS[key] ?? key}: ${parts.join(" · ")}`
    })
    .join("  |  ")
}

const targetSummary = row => {
  if (row.target_type === "permission") return row.target_permission
  if (row.target_type === "users") return `${row.target_user_ids?.length ?? 0} user(s)`
  return "Everyone in showroom"
}

const PrayerReminderSettings = () => {
  document.title = "Prayer Reminders"

  const { params, setPage } = useListParams({ page: 1 })
  const [modal, setModal] = useState({ open: false, setting: null })
  const [deleteTarget, setDeleteTarget] = useState(null)

  const queryParams = useMemo(
    () => ({ page: params.page ?? 1, per_page: 20 }),
    [params]
  )

  const { data: resp, isLoading, isRefetching, refetch } =
    usePrayerReminderSettings(queryParams)
  const rows = resp?.data?.data ?? []
  const paginationInfo = resp?.data

  const deleteMutation = useDeletePrayerReminderSetting()

  const openCreate = () => setModal({ open: true, setting: null })
  const openEdit = setting => setModal({ open: true, setting })
  const closeModal = () => {
    setModal({ open: false, setting: null })
    refetch()
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success("Prayer reminder deleted")
      setDeleteTarget(null)
      refetch()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "Showroom",
        cell: ({ row }) => row.original.showroom?.name ?? `#${row.original.showroom_id}`,
      },
      {
        header: "Prayers & offset",
        cell: ({ row }) => <small>{prayerSummary(row.original.prayers)}</small>,
      },
      { header: "Target", cell: ({ row }) => targetSummary(row.original) },
      {
        header: "Status",
        cell: ({ row }) => (
          <div className="d-flex flex-column gap-1">
            <Badge color={row.original.active ? "success" : "secondary"}>
              {row.original.active ? "Active" : "Inactive"}
            </Badge>
            {row.original.skip_friday_dhuhr && (
              <Badge color="warning">Skips Friday Dhuhr</Badge>
            )}
          </div>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              color="primary"
              onClick={() => openEdit(row.original)}
              id={`edit-pr-${row.original.id}`}
            >
              <i className="mdi mdi-pencil-outline" />
            </Button>
            <UncontrolledTooltip target={`edit-pr-${row.original.id}`}>
              Edit
            </UncontrolledTooltip>
            <Button
              size="sm"
              color="danger"
              onClick={() => setDeleteTarget(row.original)}
              id={`del-pr-${row.original.id}`}
            >
              <i className="mdi mdi-delete-outline" />
            </Button>
            <UncontrolledTooltip target={`del-pr-${row.original.id}`}>
              Delete
            </UncontrolledTooltip>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <PageWrapper title="Prayer Reminders" breadcrumbItem="Prayer Reminders">
      <Row className="mb-3 align-items-center">
        <Col>
          <p className="text-muted mb-0">
            Per-showroom five-waqt prayer reminders. Prayer times follow each
            recipient&apos;s reported device location, falling back to the
            showroom location.
          </p>
        </Col>
        <Col xs="auto">
          <Button color="primary" onClick={openCreate}>
            <i className="mdi mdi-plus" /> Add
          </Button>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={rows}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      {modal.open && (
        <PrayerReminderSettingModal
          isOpen={modal.open}
          toggle={closeModal}
          setting={modal.setting}
        />
      )}

      <DeleteModal
        show={Boolean(deleteTarget)}
        loading={deleteMutation.isPending}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteTarget(null)}
      />
    </PageWrapper>
  )
}

export default PrayerReminderSettings
